import type { FieldError, MonitoredField } from 'domain/monitored';
import type {
    ConnectivityDetailsFE,
    GeoPositionFE,
    MrmDetailsFE,
    MrmExecutionPhaseFE,
    MrmStateFE,
    MrmTriggerFE,
    MrmTypeFE,
    VehicleFE,
} from 'domain/vehicle';
import { toDate } from 'helper/time';

import type { ConnectivityStatusFieldV2, ErrorV2, MrmStateFieldV2, PositionFieldV2, VehicleV2 } from 'API';

// Local on purpose — do NOT replace with `restMapping.toFieldError`.
const toFieldError = (error?: ErrorV2 | null): FieldError | null => {
    if (error == null) {
        return null;
    }
    return { type: error.type, updatedAt: toDate(error.raisedAt) };
};

const connectivityFromV2 = (field?: ConnectivityStatusFieldV2 | null): MonitoredField<ConnectivityDetailsFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    if (field.value == null) {
        return { ttl, value: null, error };
    }
    return {
        ttl,
        value: {
            value: {
                state: field.value.state ?? 'UNKNOWN',
                expectDisconnection: field.value.expectDisconnection,
                latency: field.value.latency ?? undefined,
            },
            updatedAt: toDate(field.value.updatedAt),
        },
        error,
    };
};

const positionFromV2 = (field?: PositionFieldV2 | null): MonitoredField<GeoPositionFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    const raw = field.value?.position;
    if (field.value == null || raw == null || typeof raw.latitude !== 'number' || typeof raw.longitude !== 'number') {
        return { ttl, value: null, error };
    }
    const position: GeoPositionFE = { lat: raw.latitude, lng: raw.longitude };
    if (typeof raw.heading === 'number') {
        position.heading = raw.heading;
    }
    if (typeof raw.altitude === 'number') {
        position.altitudeMeters = raw.altitude;
    }
    return {
        ttl,
        value: { value: position, updatedAt: toDate(field.value.updatedAt) },
        error,
    };
};

// The live `VehicleV2.mrmState` carries the GraphQL enums (prefixed: `MRM_STATE_*`,
// `MRM_EXECUTION_PHASE_*`, `MRM_TYPE_*`, `MRM_TRIGGER_*`). Strip the prefix to the FE union,
// matching how `vehicleFromRestV2` maps the REST wire. Unknown members fold to 'UNKNOWN'.
const MRM_STATES: readonly MrmStateFE[] = [
    'EXECUTING_MRM',
    'MRC',
    'NONE',
    'RELEASING_MRC',
    'MRM_FAULTED',
    'MRM_ERROR',
    'UNKNOWN',
];
const MRM_EXECUTION_PHASES: readonly MrmExecutionPhaseFE[] = [
    'PRE_DECELERATION',
    'WAIT_FOR_SHOULDER',
    'SLOWDOWN_FOR_POTS',
    'PULL_TO_SHOULDER',
    'STOP_IN_LANE',
    'NOT_AVAILABLE',
    'ERROR',
    'UNKNOWN',
];
const MRM_TYPES: readonly MrmTypeFE[] = ['UNKNOWN', 'SHOULDER_STOP', 'LANE_STOP', 'ERROR', 'NOT_AVAILABLE'];
const MRM_TRIGGERS: readonly MrmTriggerFE[] = ['ONBOARD', 'OFFBOARD', 'UNKNOWN'];

const stripEnum = <T extends string>(raw: string | null | undefined, prefix: string, allowed: readonly T[]): T => {
    const stripped = raw?.startsWith(prefix) ? raw.slice(prefix.length) : raw;
    return (allowed as readonly string[]).includes(stripped ?? '') ? (stripped as T) : ('UNKNOWN' as T);
};

const mrmFromV2 = (field?: MrmStateFieldV2 | null): MonitoredField<MrmDetailsFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    const data = field.value?.data;
    if (field.value == null || data == null) {
        return { ttl, value: null, error };
    }
    return {
        ttl,
        value: {
            value: {
                state: stripEnum(data.value, 'MRM_STATE_', MRM_STATES),
                causes: (data.causes ?? []).filter((c): c is string => c != null),
                executionPhase:
                    data.executionPhase == null
                        ? undefined
                        : stripEnum(data.executionPhase, 'MRM_EXECUTION_PHASE_', MRM_EXECUTION_PHASES),
                mrmType: data.mrmType == null ? undefined : stripEnum(data.mrmType, 'MRM_TYPE_', MRM_TYPES),
                triggerBy: data.triggerBy == null ? undefined : stripEnum(data.triggerBy, 'MRM_TRIGGER_', MRM_TRIGGERS),
                startedAt: data.startedAt ?? undefined,
            },
            updatedAt: toDate(field.value.updatedAt),
        },
        error,
    };
};

export interface VehicleLiveFields {
    connectivity: MonitoredField<ConnectivityDetailsFE>;
    position: MonitoredField<GeoPositionFE>;
    mrm: MonitoredField<MrmDetailsFE>;
    latestVehicleUpdate: Date | null;
    timestamp: Date | null;
}

export const vehicleLiveFieldsFromV2 = (live: VehicleV2): VehicleLiveFields => ({
    connectivity: connectivityFromV2(live.connectivityStatus),
    position: positionFromV2(live.position),
    mrm: mrmFromV2(live.mrmState),
    latestVehicleUpdate: toDate(live.latestVehicleUpdate),
    timestamp: toDate(live.timestamp),
});

export const applyVehicleLiveUpdate = (target: VehicleFE, live: VehicleV2): void => {
    const fields = vehicleLiveFieldsFromV2(live);
    target.connectivity = fields.connectivity;
    target.position = fields.position;
    target.mrm = fields.mrm;
    target.latestVehicleUpdate = fields.latestVehicleUpdate;
    target.timestamp = fields.timestamp;
};
